import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { rapidResponseAPI } from '../services/api';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Loader2, CheckCircle, ExternalLink } from 'lucide-react';

const ReviewArticlePage = () => {
  const { responseId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [response, setResponse] = useState(null);
  const [editedContent, setEditedContent] = useState('');

  useEffect(() => {
    loadResponse();
  }, [responseId]);

  const loadResponse = async () => {
    try {
      const data = await rapidResponseAPI.get(responseId);
      setResponse(data);
      setEditedContent(data.final_article || data.ai_generated_draft || '');
      setLoading(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to load article draft",
        variant: "destructive"
      });
      navigate('/');
    }
  };

  const handleSave = async () => {
    try {
      await rapidResponseAPI.update(responseId, {
        final_article: editedContent
      });
      toast({
        title: "Draft Saved",
        description: "Your changes have been saved"
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to save changes",
        variant: "destructive"
      });
    }
  };

  const handlePublish = async () => {
    setPublishing(true);
    
    try {
      toast({
        title: "Publishing...",
        description: "Creating your article"
      });

      // Save any final edits first
      if (editedContent !== response.ai_generated_draft) {
        await rapidResponseAPI.update(responseId, {
          final_article: editedContent
        });
      }

      // Publish the article
      const result = await rapidResponseAPI.publish(responseId);

      toast({
        title: "Article Published!",
        description: "Your article is now live"
      });

      // Redirect to the published article
      setTimeout(() => {
        window.location.href = `/article/${result.slug}`;
      }, 1500);

    } catch (error) {
      console.error('Publish error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || error.message || "Failed to publish article",
        variant: "destructive"
      });
      setPublishing(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-16 text-center">
        <Loader2 className="animate-spin mx-auto mb-4" size={48} />
        <p className="text-gray-600">Loading your article draft...</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-serif font-bold mb-2">
          Review & Publish Your Counter-Article
        </h1>
        <p className="text-gray-600">
          AI has generated a draft based on your story. Review, edit if needed, and publish.
        </p>
      </div>

      {/* Original Article Reference */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">In Response To:</CardTitle>
        </CardHeader>
        <CardContent>
          <a
            href={response.original_article_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:text-blue-800 font-medium flex items-center gap-2"
          >
            {response.original_article_title}
            <ExternalLink size={16} />
          </a>
          <p className="text-sm text-gray-600 mt-2">
            Submitted by: {response.submitter_name}
          </p>
        </CardContent>
      </Card>

      {/* AI-Generated Draft */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Article Content</CardTitle>
          <CardDescription>
            Edit the AI-generated draft below. The content is in HTML format.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Textarea
            value={editedContent}
            onChange={(e) => setEditedContent(e.target.value)}
            rows={25}
            className="font-mono text-sm"
            placeholder="Article content..."
          />
          <div className="mt-4">
            <Button onClick={handleSave} variant="outline">
              Save Draft
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Preview */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Preview</CardTitle>
          <CardDescription>
            How your article will appear on the website
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div 
            className="prose max-w-none"
            dangerouslySetInnerHTML={{ __html: editedContent }}
            style={{
              fontSize: '1.125rem',
              lineHeight: '1.8',
              color: '#1f2937'
            }}
          />
        </CardContent>
      </Card>

      {/* Publish Actions */}
      <div className="sticky bottom-4 bg-white border border-gray-200 rounded-lg shadow-lg p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-semibold">Ready to publish?</p>
            <p className="text-sm text-gray-600">
              Your counter-article will be published immediately and indexed by Google
            </p>
          </div>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/submit-story')}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePublish}
              disabled={publishing}
              size="lg"
              className="bg-green-600 hover:bg-green-700"
            >
              {publishing ? (
                <>
                  <Loader2 className="animate-spin mr-2" size={16} />
                  Publishing...
                </>
              ) : (
                <>
                  <CheckCircle className="mr-2" size={16} />
                  Publish Article
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewArticlePage;
